import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectComponent } from '@ng-select/ng-select';
import { EmptyStateComponent } from '../../../components/empty-state/empty-state.component';
import { PaginationComponent } from '../../../components/pagination/pagination.component';
import { SkeletonComponent } from '../../../components/skeleton/skeleton.component';
import { UserModalComponent } from '../../../components/user-modal/user-modal.component';
import { RowLinkDirective } from '../../../directives/row-link.directive';
import { Profile } from '../../../models/profile.model';
import { UserListItemModel } from '../../../models/user-list-item.model';
import { DateToMomentPipe } from '../../../pipes/date-to-moment.pipe';
import { ProfileService } from '../../../services/profile.service';
import { UserService } from '../../../services/user.service';
import { injectIsMobile } from '../../../utils/breakpoint.util';

/** Espera antes de mandar el texto del buscador al API. */
const SEARCH_DEBOUNCE_MS = 350;

const PAGE_SIZE = 20;

/** Valor del select de estado: cadena vacia = "todos". */
type StatusFilter = '' | 'true' | 'false';

/** Valor del select de origen: cadena vacia = "todos" (BD + solo Firebase). */
type RegisteredFilter = '' | 'true' | 'false';

/**
 * Listado de usuarios.
 *
 * El API ordena siempre por `createdAt desc` y NO expone alta ni baja: los
 * usuarios se crean desde la app movil y aqui solo se editan.
 *
 * Mezcla dos orígenes: cuentas de la version anterior de KeyStoreMan, que solo
 * viven en Firebase Auth (`registered === false`), y cuentas ya migradas a la
 * base de datos. Solo estas ultimas tienen ficha de detalle y se pueden
 * editar: ver `UserListItemModel` y el filtro de origen.
 */
@Component({
  selector: 'app-users-page',
  imports: [
    RouterLink,
    DateToMomentPipe,
    PaginationComponent,
    EmptyStateComponent,
    SkeletonComponent,
    FormsModule,
    NgSelectComponent,
    RowLinkDirective,
  ],
  templateUrl: './users-page.component.html',
  styleUrl: './users-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsersPageComponent implements OnInit {
  private readonly userService = inject(UserService);
  private readonly modal = inject(NgbModal);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly profileService = inject(ProfileService);

  protected readonly loading = this.userService.loading;
  protected readonly users = this.userService.users;

  /**
   * `loaded` arranca en `false` y solo pasa a `true` cuando la peticion TERMINA
   * (con exito o con error). `loading` no basta: arranca en `false`, asi que el
   * primer render pintaria el estado vacio antes de saber si hay usuarios.
   */
  protected readonly loaded = this.userService.usersLoaded;
  protected readonly error = this.userService.usersError;
  protected readonly total = this.userService.total;
  protected readonly pageSize = this.userService.pageSize;

  /**
   * Tabla en escritorio, tarjetas en móvil. Ver `templates-page.component.ts`
   * para el porqué (renderizado condicional, no CSS que oculta un marcado
   * duplicado) y `src/app/utils/breakpoint.util.ts` para la función.
   */
  protected readonly isMobile = injectIsMobile();

  /** Mismo número de bloques que el esqueleto de filas en escritorio (8). */
  protected readonly skeletonCards = [0, 1, 2, 3, 4, 5, 6, 7];

  /** Texto tal cual lo escribe el usuario (sin debounce). */
  protected readonly search = signal('');

  /** Texto ya enviado al API. */
  private readonly appliedSearch = signal('');

  protected readonly profileFilter = signal<Profile | ''>('');
  protected readonly statusFilter = signal<StatusFilter>('');
  protected readonly registeredFilter = signal<RegisteredFilter>('');
  protected readonly page = signal(1);

  protected readonly hasFilters = computed(
    () =>
      !!this.appliedSearch() ||
      !!this.profileFilter() ||
      this.statusFilter() !== '' ||
      this.registeredFilter() !== '',
  );

  /**
   * Opciones fijas del filtro de estado. "Todos los estados" queda como item
   * REAL de la lista (no como placeholder): asi el ng-select se comporta
   * identico al `<select>` nativo que reemplaza, con el mismo valor por
   * defecto siempre visible entre las opciones.
   */
  protected readonly statusOptions: { value: StatusFilter; label: string }[] = [
    { value: '', label: 'Todos los estados' },
    { value: 'true', label: 'Activos' },
    { value: 'false', label: 'Inactivos' },
  ];

  /**
   * Opciones del filtro de origen. Igual criterio que `statusOptions`: "Todos
   * los orígenes" es un item real de la lista, no un placeholder.
   */
  protected readonly registeredOptions: { value: RegisteredFilter; label: string }[] = [
    { value: '', label: 'Todos los orígenes' },
    { value: 'true', label: 'En base de datos' },
    { value: 'false', label: 'Pendientes de migrar' },
  ];

  /** Opciones del filtro de perfil: "Todos" + el catalogo de `ProfileService`. */
  protected readonly profileOptions = computed<{ value: Profile | ''; label: string }[]>(() => [
    { value: '', label: 'Todos los perfiles' },
    ...this.profileService.profiles().map((profile) => ({
      value: profile.profile,
      label: profile.profile,
    })),
  ]);

  private searchTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.destroyRef.onDestroy(() => {
      if (this.searchTimer) clearTimeout(this.searchTimer);
    });
  }

  async ngOnInit(): Promise<void> {
    void this.profileService.ensureProfiles();
    await this.load();
  }

  protected async load(): Promise<void> {
    const status = this.statusFilter();
    const registered = this.registeredFilter();
    await this.userService.getUsers({
      search: this.appliedSearch() || undefined,
      profile: this.profileFilter() || undefined,
      status: status === '' ? undefined : status === 'true',
      registered: registered === '' ? undefined : registered === 'true',
      page: this.page(),
      pageSize: PAGE_SIZE,
    });
  }

  /** Cualquier cambio de filtro vuelve a la primera pagina. */
  private async reload(): Promise<void> {
    this.page.set(1);
    await this.load();
  }

  protected onSearch(value: string): void {
    this.search.set(value);
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.appliedSearch.set(value.trim());
      void this.reload();
    }, SEARCH_DEBOUNCE_MS);
  }

  /**
   * `null` llega cuando se limpia el ng-select con el boton de "x"; se
   * resuelve exactamente igual que elegir la opcion "Todos los perfiles".
   */
  protected onProfileFilter(value: Profile | '' | null): void {
    this.profileFilter.set(value || '');
    void this.reload();
  }

  protected onStatusFilter(value: StatusFilter | null): void {
    this.statusFilter.set(value ?? '');
    void this.reload();
  }

  protected onRegisteredFilter(value: RegisteredFilter | null): void {
    this.registeredFilter.set(value ?? '');
    void this.reload();
  }

  protected clearFilters(): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.search.set('');
    this.appliedSearch.set('');
    this.profileFilter.set('');
    this.statusFilter.set('');
    this.registeredFilter.set('');
    void this.reload();
  }

  protected onPageChange(page: number): void {
    this.page.set(page);
    void this.load();
  }

  /**
   * Solo aplica a usuarios YA registrados en la base de datos: para uno que
   * solo vive en Firebase Auth no hay nada que editar (el botón ya viene
   * `disabled` en la plantilla, esto es solo el resguardo del método).
   */
  protected async edit(user: UserListItemModel): Promise<void> {
    const userModel = user.toUserModel();
    if (!userModel) return;

    const reference = this.modal.open(UserModalComponent, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
      ariaLabelledBy: 'ks-user-modal-title',
    });

    (reference.componentInstance as UserModalComponent).setUser(userModel);

    const updated = await reference.result.catch(() => null);
    if (updated) await this.load();
  }

  /**
   * Texto corto de la columna "Aceptación legal": una sola etiqueta
   * escaneable, no un párrafo. `null` (usuario sin registrar) se resuelve con
   * el mismo "—" que las demás columnas que no aplican a esos usuarios.
   */
  protected legalStatusLabel(user: UserListItemModel): string {
    switch (user.legalAcceptanceStatus) {
      case 'both':
        return 'Aceptó ambos';
      case 'missingTerms':
        return 'Falta términos';
      case 'missingPrivacy':
        return 'Falta privacidad';
      case 'missingBoth':
        return 'Sin aceptar';
      default:
        return '—';
    }
  }

  /** Variante de `.ks-badge` para cada estatus: verde = listo, ámbar = falta uno, rojo = falta todo. */
  protected legalStatusBadgeClass(user: UserListItemModel): string {
    switch (user.legalAcceptanceStatus) {
      case 'both':
        return 'ks-badge--success';
      case 'missingTerms':
      case 'missingPrivacy':
        return 'ks-badge--warning';
      case 'missingBoth':
        return 'ks-badge--danger';
      default:
        return 'ks-badge--muted';
    }
  }

  /** Detalle del estatus para el `title` del badge (hover), sin saturar el texto visible. */
  protected legalStatusTitle(user: UserListItemModel): string {
    switch (user.legalAcceptanceStatus) {
      case 'both':
        return 'Aceptó los Términos y condiciones y el Aviso de privacidad.';
      case 'missingTerms':
        return 'Le falta aceptar los Términos y condiciones.';
      case 'missingPrivacy':
        return 'Le falta aceptar el Aviso de privacidad.';
      case 'missingBoth':
        return 'No ha aceptado los Términos y condiciones ni el Aviso de privacidad.';
      default:
        return 'Sin estatus legal: aún no está registrado en la base de datos.';
    }
  }
}
